import {
    Reducer,
    AnyAction
}                               from 'redux';
import { CustomFieldsState }    from '../../reduxTypes';
import ActionTypes              from '../../ActionTypes';

export const defaultState: CustomFieldsState = {
    customFields: {},
    allowSend: false,
    selectedField: null
};

const isDynamic = fieldKey => /^(customerName|salesRepName|dealership)$/.test(fieldKey);

// àâéêèìôùûçÀÂÆÇÉÈÊËÏÎÔŒÙÛÜŸ€àâæçéèêëïîôœùûüÿ«» are foreign language characters
// we want to include these because they'll be in French words or people's names

const lettersNumbersSpacesPeriod = /[^\w\s,'\-\+\.%&àáâäæãåāçćčèéêëeēėęîïíīįìłñńôöòóœøōõßsśšûüùúūÿžźżÀÁÂÄÆÃÅĀÇĆČÈÉÊËEĒĖĘÎÏÍĪĮÌŁÑŃÔÖÒÓŒØŌÕSSSŚŠÛÜÙÚŪŸŽŹŻ«»]/g; // Nothing but letters, numbers, spaces, 's, -s, .s
const lettersNumbersSpaces       = /[^\w\s,'\+\-%&àáâäæãåāçćčèéêëeēėęîïíīįìłñńôöòóœøōõßsśšûüùúūÿžźżÀÁÂÄÆÃÅĀÇĆČÈÉÊËEĒĖĘÎÏÍĪĮÌŁÑŃÔÖÒÓŒØŌÕSSSŚŠÛÜÙÚŪŸŽŹŻ«»]/g;   // Nothing but letters, numbers, spaces, 's, -s
const notJustSpaces              = /^\s+$/g;                                                                                                               // Can't be just spaces

const validateSpecialField = (value: string, specialValidations: string, originalValue: string) => {
    const pattern = new RegExp(specialValidations, 'g');

    return (
        originalValue !== value &&
        !value.match(pattern) &&
        !value.match(notJustSpaces) &&
        value !== ''
    );
};

const validateCustomerName = (value: string): boolean => (
    !value.match(lettersNumbersSpacesPeriod) &&
    !value.match(notJustSpaces) &&
    value !== ''
);

const validateDealership = (value: string): boolean => (
    !value.match(lettersNumbersSpacesPeriod) &&
    !value.match(notJustSpaces) &&
    value !== ''
);

const validateSalesRepName = (value: string): boolean => (
    !value.match(lettersNumbersSpacesPeriod) &&
    !value.match(notJustSpaces) &&
    value !== ''
);

const validateRegularField = (editedValue: string, originalValue: string): boolean => (
    originalValue !== editedValue &&
    !editedValue.match(lettersNumbersSpaces) &&
    !editedValue.match(notJustSpaces) &&
    editedValue !== ''
);

const hydrateFieldSet = (action, templateObject, templateIndex) => {
    return templateObject.template.fields.reduce((existingFields, field, index) => {
        const fieldObject = hydrateFieldObject(action, field, templateIndex, index);

        existingFields[fieldObject.key] = fieldObject.value;

        return existingFields;
    }, {});
};

const hydrateFieldObject = (action, field, templateIndex, index) => {
    const dynamic = isDynamic(field.name);
    const specialValidations = field.specialValidations;
    const storedValue = action.localData[templateIndex][index];

    return {
        key: field.name,
        value: {
            editedValue: dynamic ? ( storedValue ? storedValue : action.dynamicFieldInfo[field.name] ) : field.name,
            isDynamic: dynamic,
            isValid: dynamic,
            key: field.name,
            originalValue: dynamic ? action.dynamicFieldInfo[field.name] : field.name,
            specialValidations
        }
    };
};

const CustomFieldsReducer: Reducer<CustomFieldsState> = (state: CustomFieldsState = defaultState, action: AnyAction): CustomFieldsState => {
    switch (action.type) {
        case ActionTypes.CUSTOM_FIELDS_RESET:
            return defaultState;

        case ActionTypes.CUSTOM_FIELDS_MAP:
            const customFields = action.messageTemplates.map(
                (template, index) => hydrateFieldSet(action, template, index)
            ).reduce((existingFields, currentFields) => ({
                ...existingFields,
                ...currentFields
            }), {
                ...state.customFields
            });

            return {
                ...state,
                customFields,
                customFieldsLoaded: true
            };

        case ActionTypes.CUSTOM_FIELDS_UPDATE:
            // only reset to the edited value if the input is empty AND is losing focus
            const editedValue = !action.editedValue.length && action.focusChange ? state.customFields[action.fieldKey].originalValue : action.editedValue ;

            return {
                ...state,
                customFields: {
                    ...state.customFields,
                    [action.fieldKey]: {
                        ...state.customFields[action.fieldKey],
                        editedValue
                    }
                }
            };

        case ActionTypes.CUSTOM_FIELDS_VALIDATE:
            let validatedFields = state.customFields;

            Object.keys(state.customFields).map(field => {
                let result = null;

                switch (validatedFields[field].key) {
                    case 'customerName':
                        result = validateCustomerName(validatedFields[field].editedValue);
                        break;
                    case 'salesRepName':
                        result = validateSalesRepName(validatedFields[field].editedValue);
                        break;
                    case 'dealership':
                        result = validateDealership(validatedFields[field].editedValue);
                        break;
                    default:
                        result = validateRegularField(validatedFields[field].editedValue, validatedFields[field].originalValue);
                }

                if (validatedFields[field].specialValidations) {
                    result = validateSpecialField(validatedFields[field].editedValue, validatedFields[field].specialValidations, validatedFields[field].originalValue);
                }

                validatedFields = {
                    ...validatedFields,
                    [field]: {
                        ...validatedFields[field],
                        isValid: result
                    }
                };
            });

            // Filter the currently selected messageTemplate's fields to see if the corresponding fields in state are all marked 'isValid'
            const validators = action.messageTemplate.template.fields;
            const validFields = validators.filter(field => validatedFields[field.name] && validatedFields[field.name].isValid);

            return {
                ...state,
                customFields: validatedFields,
                allowSend: validFields.length === validators.length
            };

        case ActionTypes.CUSTOM_FIELDS_SELECT:
            return {
                ...state,
                selectedField: action.selectedField,
                selectedTemplate: action.templateId
            };

        default:
            return state;
    }
};

export default CustomFieldsReducer;



// WEBPACK FOOTER //
// ./src/sections/CustomFields/CustomFieldsReducer.ts